import type { CSSProperties } from "react";
import type { IImageCrop } from "@newxsystems/types";
import { createPlatformImageUrl } from "@newxsystems/platform-contracts/current/image";
import type { IImageContainerBlockProps } from "./Image.props";

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ") || undefined;

const toNumber = (value: number | `${number}` | undefined) =>
  value == null ? undefined : Number(value);

const fillImageStyle: CSSProperties = {
  bottom: 0,
  height: "100%",
  left: 0,
  position: "absolute",
  right: 0,
  top: 0,
  width: "100%",
};

const RESPONSIVE_IMAGE_WIDTHS = [320, 640, 768, 960, 1280, 1920] as const;
const COMPACT_IMAGE_WIDTHS = [160, 320] as const;

const cropAspectRatios: Record<IImageCrop["aspectRatio"], number> = {
  "3/4": 3 / 4,
  "2/3": 2 / 3,
  "3/2": 3 / 2,
  "4/3": 4 / 3,
  "16/9": 16 / 9,
  "9/16": 9 / 16,
  "5/2": 5 / 2,
  square: 1,
};

const createPlatformImageSources = (
  src: string | undefined,
  responsiveProfile: "default" | "compact" = "default",
  crop?: IImageCrop,
  quality = 85,
) => {
  if (!src) return undefined;

  const transform = (width: number) =>
    createPlatformImageUrl(src, {
      fit: crop ? "cover" : "scale-down",
      format: "auto",
      gravity: crop ? "auto" : undefined,
      height: crop ? Math.round(width / cropAspectRatios[crop.aspectRatio]) : undefined,
      quality,
      width,
    });
  const widths = responsiveProfile === "compact" ? COMPACT_IMAGE_WIDTHS : RESPONSIVE_IMAGE_WIDTHS;
  const defaultSource = transform(responsiveProfile === "compact" ? 320 : 1280);

  if (!defaultSource) return undefined;

  const srcSet = widths
    .map((width) => {
      const source = transform(width);
      return source ? `${source} ${width}w` : undefined;
    })
    .filter((source): source is string => Boolean(source))
    .join(", ");

  return { src: defaultSource, srcSet };
};

export const Image = ({
  className,
  aspectRatio,
  caption,
  crop,
  src,
  alt,
  aspectRatioClassName,
  imageClassName,
  loading,
  priority,
  responsiveProfile,
  sizes,
  srcSet,
  decoding,
  height,
  width,
  style,
  ...props
}: IImageContainerBlockProps) => {
  const imageLoading = priority === true ? "eager" : loading;
  let aspectRatioClass;
  let sizeClass = "max-w-none";

  switch (aspectRatio) {
    case "3/4":
      aspectRatioClass = "aspect-[3/4]";
      sizeClass = "max-w-2xl";
      break;
    case "4/3":
      aspectRatioClass = "aspect-[4/3]";
      break;
    case "2/3":
      aspectRatioClass = "aspect-[2/3]";
      break;
    case "3/2":
      aspectRatioClass = "aspect-[3/2]";
      break;
    case "5/2":
      aspectRatioClass = "aspect-[5/2]";
      break;
    case "16/9":
      aspectRatioClass = "aspect-[16/9]";
      break;
    case "9/16":
      aspectRatioClass = "aspect-[9/16]";
      sizeClass = "max-w-2xl";
      break;
    case "square":
      aspectRatioClass = "aspect-square";
      sizeClass = "max-w-3xl";
      break;
    case "fullwidth":
      aspectRatioClass = "";
      sizeClass = "max-w-7xl";
      break;
    case "infographic":
    case "diagram":
      aspectRatioClass = "";
      break;
  }

  const platformSources = createPlatformImageSources(
    src,
    responsiveProfile,
    crop,
    priority === true ? 75 : 85,
  );
  const imgProps = {
    ...props,
    alt,
    decoding,
    fetchPriority: priority === true ? ("high" as const) : undefined,
    loading: imageLoading,
    sizes,
    src: platformSources?.src ?? src,
    srcSet: platformSources?.srcSet ?? srcSet,
  };

  return (
    <div className={cn("overflow-hidden", sizeClass, className)} style={style}>
      <figure className="w-full h-full">
        {aspectRatio === "fullwidth" ? (
          <div className="relative w-full h-[300px] md:h-[400px] lg:h-[500px]">
            {src?.length ? (
              <img
                {...imgProps}
                className={cn("h-full w-full object-cover", imageClassName)}
                height={toNumber(height)}
                style={fillImageStyle}
                width={toNumber(width)}
              />
            ) : null}
          </div>
        ) : aspectRatio === "infographic" || aspectRatio === "diagram" ? (
          <div className="relative w-full">
            {src?.length ? (
              <img
                {...imgProps}
                className={cn("w-full h-auto object-contain", imageClassName)}
                height={toNumber(height) ?? 1200}
                width={toNumber(width) ?? 1200}
              />
            ) : null}
          </div>
        ) : (
          <div className={cn("relative w-full h-full", aspectRatioClass, aspectRatioClassName)}>
            {src?.length ? (
              <img
                {...imgProps}
                className={cn("h-full w-full object-cover", imageClassName)}
                height={toNumber(height)}
                style={fillImageStyle}
                width={toNumber(width)}
              />
            ) : null}
          </div>
        )}
        {caption ? (
          <figcaption className="mt-4 text-sm text-center text-gray-600 italic">
            {caption}
          </figcaption>
        ) : null}
      </figure>
    </div>
  );
};

Image.displayName = "App::ImageContainerBlock";
